import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { isSensitiveAnalyticsPath, maintenancePlan, redactPath, safeAnalyticsUrl, submissionEvent } from "./analyticsRules";
export { redactPath } from "./analyticsRules";

type GtagParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Send a GA4 event. Safe no-op if gtag is unavailable. */
export const trackEvent = (name: string, params: GtagParams = {}) => {
  if (isSensitiveAnalyticsPath(window.location.pathname)) return;
  window.gtag?.("event", name, {
    ...params,
    page_location: safeAnalyticsUrl(window.location.href, window.location.origin),
    page_referrer: document.referrer ? safeAnalyticsUrl(document.referrer, window.location.origin) : "",
  });
};

/** Accepted means the business notification was accepted by the email provider, not delivered to an inbox. */
export const trackSubmission = (type: string, result: unknown) => {
  const event = submissionEvent(type, result);
  if (event) trackEvent(event, { form_type: type });
};

const DOWNLOAD_RE = /\.(pdf|zip|docx?|xlsx?|pptx?|csv|txt|svg|png|jpe?g|webp|ico|json)$/i;

const REFERRALS: Record<string, string> = {
  "neti.us/odwvao": "google_workspace",
  "neti.us/dteena": "siteground",
};

const CTA_PATHS = ["/quote", "/contact", "/meeting"];

const handleClick = (e: MouseEvent) => {
  const target = e.target instanceof Element ? e.target : null;
  const el = target?.closest<HTMLElement>("a, button, [data-track]");
  if (!el || isSensitiveAnalyticsPath(window.location.pathname)) return;
  const page_path = redactPath(window.location.pathname);

  // Explicit opt-in tracking: <el data-track="event_name" data-track-label="...">
  const custom = el.dataset.track;
  if (custom) {
    trackEvent(custom, { page_path });
    return;
  }

  if (!(el instanceof HTMLAnchorElement) || !el.href) return;
  const href = el.href;

  if (href.startsWith("blob:") || href.startsWith("data:")) {
    return trackEvent("file_download", { file_extension: (el.getAttribute("download") || "").split(".").pop(), page_path });
  }
  if (href.startsWith("tel:")) return trackEvent("phone_click", { page_path });
  if (href.startsWith("mailto:")) return trackEvent("email_click", { page_path });

  let url: URL;
  try { url = new URL(href); } catch { return; }

  const referral = REFERRALS[`${url.hostname}${url.pathname}`.toLowerCase()];
  if (referral) return trackEvent("referral_click", { partner: referral, page_path });

  if (url.hostname === "help.netius.com") {
    return trackEvent("help_click", { page_path });
  }

  if (el.hasAttribute("download") || DOWNLOAD_RE.test(url.pathname)) {
    return trackEvent("file_download", {
      file_extension: url.pathname.split(".").pop()?.toLowerCase() || (el.getAttribute("download") || "").split(".").pop(),
      link_domain: url.protocol.startsWith("http") ? url.hostname : undefined,
      page_path,
    });
  }

  if (url.origin !== window.location.origin) {
    return trackEvent("outbound_click", { link_domain: url.hostname, page_path });
  }

  const plan = url.pathname === "/quote" ? maintenancePlan(url.searchParams.get("plan")) : undefined;
  if (plan) return trackEvent("maintenance_plan_select", { plan, page_path });
  if (page_path === "/tools" && url.pathname.startsWith("/tools/") && !isSensitiveAnalyticsPath(url.pathname)) {
    return trackEvent("tool_select", { tool: url.pathname.slice(7), page_path });
  }
  if (CTA_PATHS.includes(url.pathname) && url.pathname !== page_path) {
    trackEvent("cta_click", { destination: url.pathname, page_path });
  }
};

/** Mount once inside the router: SPA page views, tool views, click tracking. Leads fire from form success handlers. */
export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    document.addEventListener("click", handleClick, { capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, []);

  useEffect(() => {
    const path = redactPath(location.pathname);
    if (isSensitiveAnalyticsPath(location.pathname)) return;
    // Wait briefly (lazy pages + helmet) so react-helmet updates document.title first.
    const t = window.setTimeout(() => {
      trackEvent("page_view", {
        page_path: path,
        page_title: path === "/" ? "NETIUS — Web Development Agency in Orlando, FL" : document.title,
      });
      if (path.startsWith("/tools/")) trackEvent("tool_view", { tool: path.slice(7) });
    }, 400);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.search]);
};

export const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};
